import React, { forwardRef, useMemo } from 'react'
import ReactSelect, { MultiValue, Props, SingleValue, SelectInstance } from 'react-select'
import classNames from 'classnames'

export type SelectOption = {
    label: string
    value: string | number
}

type RawValue = number | string | string[]

export interface DropdownSelectProps extends Omit<Props, 'value' | 'defaultValue' | 'onChange'> {
    usePortal?: boolean
    /** Controlled value — dropdown reflects this on every render */
    value?: RawValue | null
    /** Uncontrolled value — only used on mount */
    defaultValue?: RawValue | null
    options: SelectOption[]
    onChange?: (value: SelectOption | SelectOption[]) => void
    placeholder?: string
    isMulti?: boolean
}

const DEFAULT_OPTIONS = [{value: '', label: ''}]

const Dropdown = forwardRef<SelectInstance, DropdownSelectProps>(({
    value,
    defaultValue,
    options = DEFAULT_OPTIONS,
    onChange,
    placeholder = 'Please select...',
    className,
    isMulti = false,
    usePortal = false,
    menuPortalTarget,
    ...otherProps
}, ref) => {
    const isControlled = value !== undefined

    // Resolve a raw value (number, string, string[]) into react-select option(s)
    const resolveValue = (raw: RawValue | null | undefined) => {
        if (raw == null) return isMulti ? [] : null

        if (isMulti) {
            // Handle multi-select case - ensure initialValue is treated as array
            const valueArray = Array.isArray(raw) ? raw : [raw]
            return valueArray.map(v => ({
                label: options.find(o => o.value === v)?.label || v,
                value: v
            }))
        } else {
            // Handle single-select case - ensure initialValue is treated as single value
            const singleValue = Array.isArray(raw) ? raw[0] : raw
            const option = options.find(o => o.value === singleValue)
            return option ? {
                label: option.label,
                value: singleValue
            } : null
        }
    }

    const resolvedValue = useMemo(
        () => isControlled ? resolveValue(value) : undefined,
        [value, options, isMulti, isControlled, resolveValue]
    )

    const resolvedDefaultValue = useMemo(
        () => !isControlled ? resolveValue(defaultValue) : undefined,
        [defaultValue, options, isMulti, isControlled, resolveValue]
    )

    const handleChange = (
        newValue: SelectOption | SelectOption[]
    ) => {
        if (!onChange) return
        onChange(newValue)
    }

    const portalRealTarget = usePortal ? (menuPortalTarget ?? document.getElementById('react-select-root')) : undefined

    return (
        <ReactSelect
            ref={ref}
            {...(isControlled
                ? { value: resolvedValue }
                : { defaultValue: resolvedDefaultValue }
            )}
            getOptionLabel={(option: SelectOption) => option.label}
            getOptionValue={(option: SelectOption) => String(option.value)}
            options={options}
            onChange={handleChange}
            placeholder={placeholder}
            isMulti={isMulti}
            menuPortalTarget={portalRealTarget}
            menuPlacement="auto"
            className={classNames('react-select', className)}
            classNamePrefix="select"
            {...otherProps}
        />
    )
})

export { Dropdown }
